<template>
  <section
    class="h-100"
    :class="[skinClasses]"
  >
    <component class="h-100" :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </section>
</template>

<script>
import ScrollToTop from '@/core/components/scroll-to-top/ScrollToTop.vue'
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { getCurrentInstance, onBeforeMount, watch } from 'vue'
import useAppConfig from '@/core/app-config/useAppConfig'
import { useWindowSize, useCssVar } from '@vueuse/core'
import store from '@/store'
import { getUserData } from '@/auth/utils'
import { mixins } from '@/mixins'
import moment from 'moment-timezone'
import useSocket from '@/composables/socket'
import useWindow from '@/composables/window'

const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    let faviconUrl = null
    const user = getUserData()
    if (user && user.program_version === 'profman_dental') {
      faviconUrl = $themeConfig.app.appFaviconProfmanDental
    } else {
      faviconUrl = $themeConfig.app.appFaviconProfmanHealth
    }

    document.getElementById('favicon').href = faviconUrl;

    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout
    const { connectToUserSocket } = useSocket()
    const { canUseUserOnlineChecker, setUserOnlineChecker } = useWindow()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })
    const timezone = mixins.methods.getUserTimezone()
    if (timezone) {
      moment.tz.setDefault(timezone)
    }
    const setBVModal = () => store.commit('application/SET_BV_MODAL', getCurrentInstance().proxy.$bvModal)
    const initUserOnlineChecker = () => {
      if (canUseUserOnlineChecker.value) {
        setUserOnlineChecker()
      }
    }

    onBeforeMount(() => {
      setBVModal()
      connectToUserSocket()
      initUserOnlineChecker()
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  }
}
</script>
